// Placeholder AI service layer.
// Every function here returns a mock but is shaped so the implementation
// can be swapped for real AI Gateway (createServerFn + Lovable AI) calls later
// without touching the UI.

export type AiStatus = "placeholder" | "ready";
export const AI_STATUS: AiStatus = "placeholder";

async function fakeDelay<T>(value: T, ms = 900): Promise<T> {
  await new Promise((r) => setTimeout(r, ms));
  return value;
}

/* ---------- CV Builder ---------- */
export type CvInput = {
  fullName: string;
  headline: string;
  summary: string;
  experience: string;
  education: string;
  skills: string;
};

export async function generateCv(input: CvInput): Promise<string> {
  return fakeDelay(
    `# ${input.fullName || "Your Name"}\n\n**${input.headline || "Professional headline"}**\n\n## Summary\n${input.summary || "A results-driven professional with a passion for impact."}\n\n## Experience\n${input.experience || "• Recent role — Company — Dates\n  - Impact bullet\n  - Impact bullet"}\n\n## Education\n${input.education || "• Degree — Institution — Year"}\n\n## Skills\n${input.skills || "TypeScript, Leadership, Communication"}\n`,
  );
}

/* ---------- ATS Checker ---------- */
export type AtsReport = {
  score: number;
  matched: string[];
  missing: string[];
  suggestions: string[];
};

export async function checkAts(resume: string, jobDescription: string): Promise<AtsReport> {
  const r = resume.toLowerCase();
  const jdWords = Array.from(
    new Set(
      jobDescription
        .toLowerCase()
        .split(/[^a-z0-9+#.]+/)
        .filter((w) => w.length > 3),
    ),
  );
  const keywords = jdWords.slice(0, 12);
  const matched = keywords.filter((k) => r.includes(k));
  const missing = keywords.filter((k) => !r.includes(k));
  const score = keywords.length ? Math.round((matched.length / keywords.length) * 100) : 72;
  return fakeDelay({
    score,
    matched,
    missing,
    suggestions: [
      "Use standard section headings (Experience, Education, Skills).",
      "Include measurable achievements with numbers and percentages.",
      "Add missing keywords naturally to your experience section.",
      "Save as PDF with selectable text — avoid scanned images.",
    ],
  });
}

/* ---------- Cover Letter ---------- */
export async function generateCoverLetter(
  role: string,
  company: string,
  highlights: string,
): Promise<string> {
  return fakeDelay(
    `Dear Hiring Team at ${company || "the company"},\n\nI'm excited to apply for the ${role || "role"} position. ${highlights || "My background aligns closely with the responsibilities in your job description."}\n\nOver the past several years I've delivered measurable outcomes for cross-functional teams and I'd love to bring the same energy to your organization.\n\nThank you for your consideration — I look forward to speaking soon.\n\nSincerely,\n[Your name]`,
  );
}

/* ---------- Translator ---------- */
export async function translateResume(text: string, target: string): Promise<string> {
  return fakeDelay(
    `[${target}]\n\n${text || "Paste your resume text to see a translation."}\n\n— Translated by AI (placeholder)`,
  );
}

/* ---------- Interview Coach ---------- */
export type InterviewTurn = { role: "coach" | "you"; content: string };
export async function interviewReply(history: InterviewTurn[], question: string): Promise<string> {
  const _ = history.length;
  return fakeDelay(
    `Good response. Consider structuring it with the STAR method: Situation, Task, Action, Result. For example, "${question.slice(0, 60)}..." → describe the context briefly, the action you owned, and the measurable outcome.`,
  );
}

/* ---------- Career Advisor ---------- */
export async function askAdvisor(question: string): Promise<string> {
  return fakeDelay(
    `Great question. Based on the trends we're seeing on GOH, here's how I'd think about "${question.slice(0, 80)}":\n\n1. Clarify your 12-month goal.\n2. Identify two target markets and their visa pathways.\n3. Close skill gaps with focused, portfolio-driven projects.\n4. Ship a resume tailored to each target market.`,
  );
}

/* ---------- Job Match Score ---------- */
export async function scoreJobMatch(
  profile: string,
  jobDescription: string,
): Promise<{ score: number; reasons: string[] }> {
  const overlap =
    profile && jobDescription
      ? Math.min(100, 50 + ((profile.length + jobDescription.length) % 50))
      : 65;
  return fakeDelay({
    score: overlap,
    reasons: [
      "Skills alignment: strong match on core stack",
      "Experience level: within the range requested",
      "Location & work authorization: fit likely",
      "Growth potential: role offers clear next step",
    ],
  });
}

/* ---------- Opportunity Score ---------- */
export type OpportunityScore = {
  score: number;
  verdict: "Exceptional" | "Strong" | "Worth exploring" | "Low fit";
  factors: { label: string; weight: number }[];
};
export async function scoreOpportunity(
  profile: string,
  opportunity: string,
): Promise<OpportunityScore> {
  const base = ((profile.length + opportunity.length) % 40) + 55;
  const score = Math.min(99, base);
  const verdict: OpportunityScore["verdict"] =
    score >= 85
      ? "Exceptional"
      : score >= 70
        ? "Strong"
        : score >= 55
          ? "Worth exploring"
          : "Low fit";
  return fakeDelay({
    score,
    verdict,
    factors: [
      { label: "Skill relevance", weight: Math.min(100, score + 5) },
      { label: "Career trajectory fit", weight: score },
      { label: "Location & mobility", weight: Math.max(30, score - 10) },
      { label: "Compensation alignment", weight: Math.max(40, score - 5) },
      { label: "Long-term growth", weight: Math.min(100, score + 8) },
    ],
  });
}

/* ---------- Career Roadmap ---------- */
export type RoadmapStep = { horizon: string; title: string; actions: string[] };
export async function buildRoadmap(
  currentRole: string,
  targetRole: string,
  years: number,
): Promise<RoadmapStep[]> {
  const y = Math.max(1, Math.min(10, years || 3));
  return fakeDelay([
    {
      horizon: "Next 30 days",
      title: `Anchor from ${currentRole || "your current role"}`,
      actions: [
        "Audit skills against target JD",
        "Pick 2 portfolio projects",
        "Refresh CV headline",
      ],
    },
    {
      horizon: "3 months",
      title: "Close top skill gaps",
      actions: [
        "Ship 1 flagship project",
        "Complete 1 certification",
        "Grow network by 20 relevant contacts",
      ],
    },
    {
      horizon: "6 months",
      title: "Position for interviews",
      actions: [
        "Apply to 5 target companies",
        "Do 3 mock interviews/week",
        "Publish 2 case studies",
      ],
    },
    {
      horizon: `${y} year${y > 1 ? "s" : ""}`,
      title: `Land ${targetRole || "your next role"}`,
      actions: [
        "Negotiate offer with market data",
        "Onboard with a 30/60/90 plan",
        "Set the next roadmap",
      ],
    },
  ]);
}

/* ---------- Skill Gap Analysis ---------- */
export type SkillGap = {
  have: string[];
  missing: string[];
  priorities: { skill: string; effort: "Low" | "Medium" | "High" }[];
};
export async function analyzeSkillGap(
  currentSkills: string,
  targetRole: string,
): Promise<SkillGap> {
  const have = currentSkills
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 8);
  const pool = [
    "System design",
    "TypeScript",
    "Cloud (AWS/GCP)",
    "Data modeling",
    "Leadership",
    "Stakeholder mgmt",
    "CI/CD",
    "Observability",
    "Product sense",
  ];
  const missing = pool
    .filter((p) => !have.some((h) => h.toLowerCase().includes(p.toLowerCase().split(" ")[0])))
    .slice(0, 5);
  return fakeDelay({
    have,
    missing,
    priorities: missing.map((skill, i) => ({
      skill: `${skill}${targetRole ? ` (for ${targetRole})` : ""}`,
      effort: i % 3 === 0 ? "High" : i % 2 === 0 ? "Medium" : "Low",
    })),
  });
}

/* ---------- Salary Estimator ---------- */
export type SalaryEstimate = {
  low: number;
  mid: number;
  high: number;
  currency: string;
  notes: string[];
};
export async function estimateSalary(
  role: string,
  country: string,
  years: number,
): Promise<SalaryEstimate> {
  const base = 40000 + role.length * 900 + country.length * 1200 + years * 4200;
  return fakeDelay({
    low: Math.round(base * 0.85),
    mid: Math.round(base),
    high: Math.round(base * 1.25),
    currency: "USD",
    notes: [
      "Estimates based on market medians",
      "Adjust for equity, bonus, and cost of living",
      "Remote roles trend 10–15% lower than local senior bands",
    ],
  });
}

/* ---------- Country Recommendation ---------- */
export type CountryPick = { country: string; code: string; score: number; why: string };
export async function recommendCountries(
  profession: string,
  priorities: string,
): Promise<CountryPick[]> {
  const _ = profession + priorities;
  return fakeDelay([
    {
      country: "Germany",
      code: "de",
      score: 92,
      why: "EU Blue Card pathway, strong demand, English-friendly employers.",
    },
    {
      country: "Canada",
      code: "ca",
      score: 89,
      why: "Express Entry favors your profile; PR in 12–18 months.",
    },
    {
      country: "Netherlands",
      code: "nl",
      score: 86,
      why: "Highly Skilled Migrant visa via sponsors; great work-life balance.",
    },
    {
      country: "Australia",
      code: "au",
      score: 82,
      why: "Skilled Independent 189 aligns with your experience.",
    },
    {
      country: "United Arab Emirates",
      code: "ae",
      score: 77,
      why: "Tax-free income and Golden Visa options for specialists.",
    },
  ]);
}

/* ---------- Visa Readiness ---------- */
export type VisaReadiness = {
  score: number;
  ready: string[];
  todo: string[];
  recommendedVisa: string;
};
export async function scoreVisaReadiness(
  country: string,
  hasOffer: boolean,
  degree: string,
  yearsExp: number,
): Promise<VisaReadiness> {
  let score = 40;
  if (hasOffer) score += 25;
  if (degree) score += 15;
  score += Math.min(20, yearsExp * 3);
  return fakeDelay({
    score: Math.min(99, score),
    ready: [hasOffer ? "Employer offer in hand" : "", degree ? "Recognized degree" : ""].filter(
      Boolean,
    ),
    todo: [
      !hasOffer ? "Secure a sponsoring employer" : "Collect payslips (last 3 months)",
      "Get passport validity ≥ 12 months",
      "Prepare English proficiency test (if required)",
      "Gather police clearance certificate",
    ],
    recommendedVisa: country ? `${country} Skilled Worker visa` : "Skilled Worker visa",
  });
}

/* ---------- Scholarship Eligibility ---------- */
export type ScholarshipEligibility = {
  eligible: boolean;
  score: number;
  reasons: string[];
  nextSteps: string[];
};
export async function checkScholarshipEligibility(
  country: string,
  level: string,
  gpa: string,
  fundingNeed: string,
): Promise<ScholarshipEligibility> {
  const gpaNum = parseFloat(gpa) || 3.2;
  const score = Math.round(
    Math.min(100, gpaNum * 22 + (level ? 10 : 0) + (country ? 8 : 0) + (fundingNeed ? 6 : 0)),
  );
  return fakeDelay({
    eligible: score >= 60,
    score,
    reasons: [
      `GPA ${gpaNum.toFixed(2)} — ${gpaNum >= 3.5 ? "competitive" : "acceptable for many programs"}`,
      level ? `Level of study: ${level}` : "Level of study not specified",
      country ? `Preferred destination: ${country}` : "Destination flexible",
    ],
    nextSteps: [
      "Draft motivation letter",
      "Request 2 recommendation letters",
      "Prepare research proposal (if applicable)",
      "Shortlist 5 aligned scholarships",
    ],
  });
}

/* ---------- Career Growth Planner ---------- */
export type GrowthPlan = { quarter: string; focus: string; kpis: string[] }[];
export async function planGrowth(role: string, ambition: string): Promise<GrowthPlan> {
  const _ = role + ambition;
  return fakeDelay([
    {
      quarter: "Q1",
      focus: "Foundations",
      kpis: ["Ship 1 project", "Read 3 books", "Meet 10 peers"],
    },
    {
      quarter: "Q2",
      focus: "Visibility",
      kpis: ["Publish 4 posts", "Speak at 1 event", "Grow network 25%"],
    },
    {
      quarter: "Q3",
      focus: "Depth",
      kpis: ["Lead 1 initiative", "Mentor 2 juniors", "Ship advanced project"],
    },
    {
      quarter: "Q4",
      focus: "Leverage",
      kpis: ["Negotiate promotion/offer", "Publish year-in-review", "Set next-year OKRs"],
    },
  ]);
}

/* ---------- Weekly Career Report ---------- */
export type WeeklyReport = {
  weekOf: string;
  headline: string;
  metrics: { label: string; value: string; delta: string }[];
  highlights: string[];
  focusNextWeek: string[];
};
export async function generateWeeklyReport(): Promise<WeeklyReport> {
  const d = new Date();
  return fakeDelay({
    weekOf: d.toDateString(),
    headline: "Steady momentum — 3 strong matches this week",
    metrics: [
      { label: "New matches", value: "12", delta: "+4" },
      { label: "Applications", value: "5", delta: "+2" },
      { label: "Profile views", value: "38", delta: "+15%" },
      { label: "Interviews", value: "1", delta: "+1" },
    ],
    highlights: [
      "Your CV was viewed by 3 verified employers",
      "2 scholarships opened matching your profile",
      "Skill gap in 'System design' closed by 20%",
    ],
    focusNextWeek: [
      "Complete 2 mock interviews",
      "Apply to your top 3 opportunity picks",
      "Update passport & visa reminders",
    ],
  });
}

/* ---------- Career Progress Analytics ---------- */
export type ProgressSeries = { label: string; points: { week: string; value: number }[] };
export async function loadProgressAnalytics(): Promise<{
  overallScore: number;
  streakWeeks: number;
  series: ProgressSeries[];
  milestones: { date: string; label: string }[];
}> {
  const weeks = Array.from({ length: 8 }, (_, i) => `W${i + 1}`);
  return fakeDelay({
    overallScore: 78,
    streakWeeks: 6,
    series: [
      { label: "Applications", points: weeks.map((w, i) => ({ week: w, value: 2 + i })) },
      { label: "Skill score", points: weeks.map((w, i) => ({ week: w, value: 50 + i * 4 })) },
      { label: "Profile views", points: weeks.map((w, i) => ({ week: w, value: 10 + i * 6 })) },
    ],
    milestones: [
      { date: "Wk 2", label: "Completed AI CV Builder" },
      { date: "Wk 4", label: "First interview scheduled" },
      { date: "Wk 6", label: "Skill gap reduced by 30%" },
    ],
  });
}
